import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import CartDrawer from "@/components/feature/CartDrawer";
import HomeIntro from "@/components/feature/HomeIntro";
import KitEmpreendedorModal from "@/components/feature/KitEmpreendedorModal";
import { useCart } from "@/context/CartContext";
import { categories as mockCategories, products as mockProducts, type Product } from "@/mocks/products";
import { fetchBestSellers, fetchCatalogCached, fetchNuvemshopCategories, isNuvemshopEnabled, type NuvemshopCategory } from "@/services/nuvemshop";
import { PINNED_PRODUCT_ID, PROMO_COUNT, dailyPicks, storeDay } from "@/utils/collections";
import { defaultVariant, defaultVariantLabel, variantImage, variantMaxQuantity } from "@/utils/variants";
import { instagramPosts } from "@/data/instagramPosts";
import { SHOW_SAMPLE_TESTIMONIALS, sampleTestimonials, testimonials } from "@/data/testimonials";
import { CategoryStrip, FeatureBanner, Hero, InstagramGrid, ProductCarousel, Testimonials, type LpCategory } from "./landing/LpSections";
import "./landing/landing.css";

const SECTION_SIZE = 12;

// A página inicial é a vitrine; o catálogo completo, com filtros, fica em /produtos.
// Endereços antigos como /?categoria=ID ou /?colecao=promocoes seguem funcionando.
export default function Landing() {
  const [params] = useSearchParams();
  const location = useLocation();
  if (params.has("categoria") || params.has("colecao") || params.has("q")) {
    return <Navigate to={`/produtos${location.search}`} replace />;
  }
  return <LandingPage />;
}

function LandingPage() {
  const { addItem, openCart } = useCart();
  const live = isNuvemshopEnabled();
  const [products, setProducts] = useState<Product[]>(live ? [] : mockProducts);
  const [categories, setCategories] = useState<NuvemshopCategory[]>(live ? [] : mockCategories.map((c) => ({ id: c.id, label: c.label })));
  const [bestSellers, setBestSellers] = useState<Product[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(live);
  const [loadingBest, setLoadingBest] = useState(live);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback((attempt = 0) => {
    // Cada bloco carrega por conta própria: se um falhar, a página não fica sem os outros.
    fetchCatalogCached()
      .then((items) => { setProducts(items.filter((item) => item.inStock !== false)); setLoadingCatalog(false); })
      .catch(() => {
        if (attempt === 0) { window.setTimeout(() => load(1), 1500); return; }
        setLoadingCatalog(false);
      });
    fetchBestSellers(SECTION_SIZE).then(setBestSellers).catch(() => { /* seção some */ }).finally(() => setLoadingBest(false));
    fetchNuvemshopCategories().then(setCategories).catch(() => { /* faixa some */ });
  }, []);
  useEffect(() => { if (live) load(); }, [live, load]);

  useEffect(() => {
    document.title = "Rica Tanto | Semijoias e Acessórios Dourados que Valorizam sua História";
  }, []);

  const day = storeDay();
  // Promoções: as mesmas 25 do dia do menu (o começo da lista).
  const promos = useMemo(() => dailyPicks(products, PROMO_COUNT, day).slice(0, SECTION_SIZE), [products, day]);
  const recommended = useMemo(() => dailyPicks(products, 10, day, "recomendados"), [products, day]);
  const stripCategories = useMemo<LpCategory[]>(() => {
    const newestFirst = [...products].sort((a, b) => b.id - a.id);
    return categories
      .map((category) => {
        const sample = newestFirst.find((product) => (product.categoryIds ?? []).includes(Number(category.id)));
        return { id: category.id, label: category.label, image: sample?.image, hasProducts: Boolean(sample) };
      })
      .filter((category) => category.hasProducts || !live)
      .map(({ id, label, image }) => ({ id, label, image }));
  }, [categories, products, live, loadingCatalog]);

  const handleAdd = (product: Product) => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image: variantImage(product, defaultVariant(product)),
      variantId: product.variantId,
      variantLabel: defaultVariantLabel(product),
      maxQuantity: variantMaxQuantity(defaultVariant(product)),
    });
    openCart();
    setToast(`${product.name} adicionado à sacola`);
    window.setTimeout(() => setToast(null), 2600);
  };

  // Depoimentos: só os reais vão ao ar; os de exemplo aparecem apenas na visualização local.
  const realTestimonials = testimonials.length > 0;
  const testimonialItems = realTestimonials ? testimonials : import.meta.env.DEV || SHOW_SAMPLE_TESTIMONIALS ? sampleTestimonials : [];

  // Instagram: os posts da loja (src/data/instagramPosts.ts); na visualização local, sem posts, mostra fotos de produtos.
  const instaItems = instagramPosts.length > 0
    ? instagramPosts
    : import.meta.env.DEV
      ? products.slice(0, 6).map((product) => ({ image: product.image, url: "https://www.instagram.com/lojaricatanto/", alt: product.name }))
      : [];
  const pinned = products.find((product) => product.id === PINNED_PRODUCT_ID);

  return (
    <div className="landing flex min-h-screen flex-col">
      <HomeIntro />
      <KitEmpreendedorModal product={pinned} />
      <Header variant="overlay" />
      <main>
        <Hero />
        <CategoryStrip categories={stripCategories} loading={loadingCatalog} />
        <ProductCarousel
          id="lp-promos"
          eyebrow="Promoções"
          title={<>DESCONTOS <em>ESPECIAIS</em></>}
          subtitle="Aproveite nossas ofertas e garanta seus acessórios favoritos com preços imperdíveis."
          linkHref="/produtos?colecao=promocoes"
          linkLabel="Ver todas as promoções"
          products={promos}
          loading={loadingCatalog}
          onAdd={handleAdd}
          tone="light"
        />
        <FeatureBanner />
        <ProductCarousel
          id="lp-best"
          title="Mais Vendidos"
          linkHref="/produtos?colecao=vendidos"
          linkLabel="Ver todos"
          products={bestSellers}
          loading={loadingBest}
          onAdd={handleAdd}
          ranked
        />
        <Testimonials items={testimonialItems} />
        <ProductCarousel
          id="lp-reco"
          title="Recomendados para você"
          subtitle="Selecionamos produtos que combinam com seu estilo"
          linkHref="/produtos"
          linkLabel="Ver todos"
          products={recommended}
          loading={loadingCatalog}
          onAdd={handleAdd}
        />
        <InstagramGrid posts={instaItems} sample={!instagramPosts.length && import.meta.env.DEV} />
      </main>
      <Footer />
      <CartDrawer />
      {toast && (
        <div className="fixed bottom-6 left-1/2 z-40 flex w-[calc(100vw-2rem)] max-w-sm -translate-x-1/2 items-center gap-3 rounded-lg bg-foreground-950 px-4 py-3 text-sm text-background-50 shadow-lg" role="status">
          <span className="flex h-5 w-5 items-center justify-center text-primary-400"><i className="ri-checkbox-circle-line text-lg"></i></span>
          {toast}
        </div>
      )}
    </div>
  );
}
